import axios from "axios";
import { isTokenExpired } from "../../utils/tokenUtils";

let API_URL =
  process.env.REACT_APP_Environment === "development"
    ? process.env.REACT_APP_API_URL
    : process.env.REACT_APP_API_URL_Production;

export { API_URL };

const axiosInstance = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  timeout: 10000,
  validateStatus: function (status) {
    return status < 500; // Accept any status code less than 500
  }
});

const mutatingMethods = new Set(["post", "put", "patch", "delete"]);
let isForceLogoutInProgress = false;
let refreshTokenPromise = null;

const isAuthEndpoint = (url = "") => {
  // Match with or without a leading slash (APIs.LOGIN historically omitted it).
  const normalized = String(url || "").replace(/^\/+/, "");
  return (
    normalized.startsWith("api/Auth/Login") ||
    normalized.startsWith("api/Auth/RefreshToken") ||
    normalized.startsWith("api/Auth/AutoLogin") ||
    normalized.startsWith("api/Auth/Logout")
  );
};

const clearClientAuthStorage = () => {
  try {
    localStorage.removeItem("user");
    localStorage.removeItem("keepMeLogin");
    localStorage.removeItem("itemGridPreferences");
    sessionStorage.clear();
  } catch (_) {}
  try {
    document.cookie = "csrf_token=; Max-Age=0; path=/";
  } catch (_) {}
};

const triggerForceLogout = () => {
  if (isForceLogoutInProgress) {
    return;
  }

  isForceLogoutInProgress = true;
  // Capture path before listeners run so a soft navigate cannot skip the hard reload.
  const wasOnLoginPage = window.location.pathname.includes("/user/login");

  // Clear storage here so logout works even if TokenExpiryChecker is unmounted.
  clearClientAuthStorage();
  window.dispatchEvent(new CustomEvent("forceLogout"));

  if (!wasOnLoginPage) {
    // Hard reload resets in-memory force-logout state so re-login can hit the server.
    window.location.replace("/user/login");
  } else {
    // Already on login; clear the guard so the next login attempt is not blocked client-side.
    clearForceLogoutState();
  }
};

const clearForceLogoutState = () => {
  isForceLogoutInProgress = false;
};

const tryRefreshToken = async () => {
  if (!refreshTokenPromise) {
    refreshTokenPromise = axiosInstance.post(`/api/Auth/RefreshToken`, {
      clientType: "web",
    });
  }

  try {
    return await refreshTokenPromise;
  } finally {
    refreshTokenPromise = null;
  }
};

const ensureCsrfToken = async () => {
  let csrfToken = getCsrfTokenForRequest();
  if (csrfToken) {
    return csrfToken;
  }

  try {
    const refreshResponse = await tryRefreshToken();
    if (refreshResponse?.status === 401) {
      return null;
    }
    if (refreshResponse?.data?.status) {
      persistCsrfFromAuthResponse(refreshResponse);
      csrfToken = getCsrfTokenForRequest();
    }
  } catch (_) {
    // Best effort only; caller decides whether to proceed.
  }

  return csrfToken || null;
};

const handleUnauthorizedResponse = async (originalRequest, fallbackError) => {
  if (originalRequest?.skipUnauthorizedRedirect) {
    return Promise.reject(fallbackError);
  }

  if (!originalRequest || originalRequest._retry || isAuthEndpoint(originalRequest.url)) {
    triggerForceLogout();
    return Promise.reject(fallbackError);
  }

  originalRequest._retry = true;

  try {
    const refreshResponse = await tryRefreshToken();
    // validateStatus accepts 401 as a resolved response — treat it as failure.
    if (refreshResponse?.status === 401) {
      triggerForceLogout();
      return Promise.reject(fallbackError);
    }
    if (refreshResponse?.data?.status) {
      return axiosInstance(originalRequest);
    }
  } catch (refreshError) {
    // Refresh failed; force logout below.
  }

  triggerForceLogout();
  return Promise.reject(fallbackError);
};

const getCookieValue = (name) => {
  const cookieValue = document.cookie
    .split("; ")
    .find((entry) => entry.startsWith(`${name}=`));
  return cookieValue ? decodeURIComponent(cookieValue.split("=")[1]) : null;
};

/** Prefer sessionStorage (set from login/refresh JSON) so X-CSRF-Token is sent when API cookies are not visible to document.cookie (e.g. API on another port). */
const getCsrfTokenForRequest = () => {
  try {
    const fromSession = sessionStorage.getItem("csrf_token");
    if (fromSession) {
      return fromSession;
    }
  } catch (_) {}
  return getCookieValue("csrf_token");
};

const persistCsrfFromAuthResponse = (response) => {
  const payload = response?.data?.data ?? response?.data?.Data;
  const token = payload?.csrfToken ?? payload?.CsrfToken;
  if (token && typeof token === "string") {
    try {
      sessionStorage.setItem("csrf_token", token);
    } catch (_) {}
  }

  // Keep the access token as an Authorization-header fallback for browsers
  // that block the cross-site HttpOnly session cookies.
  const accessToken = payload?.token ?? payload?.Token;
  if (accessToken && typeof accessToken === "string") {
    try {
      sessionStorage.setItem("bo_access_token", accessToken);
    } catch (_) {}
  }
};

const getStoredAccessToken = () => {
  try {
    return sessionStorage.getItem("bo_access_token");
  } catch (_) {
    return null;
  }
};

const clearStoredAccessToken = () => {
  try {
    sessionStorage.removeItem("bo_access_token");
  } catch (_) {}
};

// Request interceptor for auth fallback header and CSRF header in cookie mode
axiosInstance.interceptors.request.use((config) => {
  if (isForceLogoutInProgress && !isAuthEndpoint(config?.url)) {
    return Promise.reject(new Error("Session expired. Redirecting to login."));
  }

  // Bearer fallback: the backend prefers this header and falls back to the
  // HttpOnly cookie, so requests stay authenticated when cookies are blocked.
  // Skipped for login/refresh, which must not carry a stale token (Logout still needs it).
  // Never send an expired JWT — it would override a still-valid cookie on the API.
  const isTokenIssuingEndpoint = /api\/Auth\/(Login|RefreshToken|AutoLogin)/i.test(config?.url || "");
  if (!isTokenIssuingEndpoint) {
    const accessToken = getStoredAccessToken();
    if (accessToken) {
      if (isTokenExpired(accessToken)) {
        clearStoredAccessToken();
      } else {
        config.headers["Authorization"] = `Bearer ${accessToken}`;
      }
    }
  }

  if (!mutatingMethods.has((config.method || "get").toLowerCase()) || isAuthEndpoint(config?.url)) {
    return config;
  }

  return ensureCsrfToken().then((csrfToken) => {
    if (csrfToken) {
      config.headers["X-CSRF-Token"] = csrfToken;
    }
    return config;
  });
}, (error) => {
  return Promise.reject(error);
});

// Response interceptor to handle 401 errors and token refresh
axiosInstance.interceptors.response.use(
  async (response) => {
    persistCsrfFromAuthResponse(response);

    if (isAuthEndpoint(response?.config?.url) && response?.data?.status) {
      clearForceLogoutState();
    }

    if (response?.status === 401) {
      return handleUnauthorizedResponse(response.config, {
        response,
        message: "Unauthorized"
      });
    }

    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 Unauthorized errors
    if (
      error.response?.status === 401 &&
      !originalRequest?.url?.includes("/api/Auth/RefreshToken")
    ) {
      return handleUnauthorizedResponse(originalRequest, error);
    }

    // Self-heal stale csrf/session mismatch once on mutating requests.
    if (
      error.response?.status === 403 &&
      mutatingMethods.has((originalRequest?.method || "get").toLowerCase()) &&
      !originalRequest?._csrfRetry &&
      !isAuthEndpoint(originalRequest?.url)
    ) {
      originalRequest._csrfRetry = true;
      const csrfToken = await ensureCsrfToken();
      if (csrfToken) {
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers["X-CSRF-Token"] = csrfToken;
        return axiosInstance(originalRequest);
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
export { clearForceLogoutState, triggerForceLogout };
